import type { Project } from '~/features/projects/types'

import { PageContent, PageHeader, PageTitle } from '~/elements/PageHeader'
import {
  useProjectIndexesQuery,
  useProjectTextIndexesQuery
} from '~/features/projects/hooks/useProjectQueries'
import { AddTextIndexDialog, TextIndexesList } from '~/features/indexes/components/TextIndexes'
import { AddIndexDialog } from '~/features/indexes/components/AddIndex'
import { IndexesList } from '~/features/indexes/components/IndexesList'
import { SuggestedIndexesCard } from '~/features/indexes/components/SuggestedIndexes'
import { NothingFound } from '~/elements/NothingFound'
import { usePlatformSettings } from '~/features/auth/hooks/useAuthQueries'
import { Button } from '~/elements/Button'
import { ExternalLink, Plus } from 'lucide-react'

const EMBEDDING_INDEX_DOCS_URL = 'https://docs.rushdb.com/learn/semantic-search/manage-indexes'

export function ProjectIndexes({ projectId }: { projectId: Project['id'] }) {
  const { data: indexes, isPending: loading } = useProjectIndexesQuery()
  const { data: platformSettings, isPending: settingsPending } = usePlatformSettings()
  const embeddingEnabled = platformSettings?.embeddingEnabled === true
  const { data: textIndexes, isPending: textIndexesLoading } = useProjectTextIndexesQuery()

  return (
    <>
      <PageHeader className="items-start" contained>
        <div className="flex max-w-3xl flex-col gap-2">
          <PageTitle>Search Indexes</PageTitle>
          <p className="text-sm leading-6 text-content2">
            Semantic indexes make selected text fields searchable by meaning; text indexes make them
            searchable by keywords. Each index is scoped to one label, backfills matching records, and powers
            search from the Records page and API.
          </p>
          <a
            className="inline-flex w-fit items-center gap-2 text-sm text-content2 transition hover:text-content"
            href={EMBEDDING_INDEX_DOCS_URL}
            rel="noreferrer"
            target="_blank"
          >
            Read the docs <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        {settingsPending || embeddingEnabled ?
          <AddIndexDialog
            existingIndexes={indexes}
            projectId={projectId}
            trigger={
              <Button variant="primary">
                <Plus />
                New Index
              </Button>
            }
          />
        : null}
      </PageHeader>
      <PageContent className="gap-8" contained>
        <section className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-content">Text indexes</h2>
              <p className="text-sm text-content2">
                Keyword search (BM25) over one text property of a label, or all of its text.
              </p>
            </div>
            <AddTextIndexDialog
              existingIndexes={textIndexes}
              projectId={projectId}
              trigger={
                <Button variant="outline">
                  <Plus />
                  New Text Index
                </Button>
              }
            />
          </div>
          <TextIndexesList data={textIndexes} loading={textIndexesLoading} />
        </section>
        {settingsPending || embeddingEnabled ?
          <>
            <section className="flex flex-col gap-3">
              <div>
                <h2 className="text-lg font-semibold text-content">Semantic indexes</h2>
                <p className="text-sm text-content2">
                  Monitor index status and backfill progress for searchable text fields in this project.
                </p>
              </div>
              <IndexesList data={indexes} loading={loading} />
            </section>
            <section className="flex flex-col gap-3">
              <SuggestedIndexesCard
                existingIndexes={indexes}
                indexesLoading={loading}
                projectId={projectId}
              />
            </section>
          </>
        : <NothingFound title="Semantic search is disabled" />}
      </PageContent>
    </>
  )
}
